import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import Login from './components/Login';
import AcceptInvitation from './components/AcceptInvitation';
import './App.css';
import Dashboard from './components/Dashboard';
import RolesPage from './components/roles/RolesPage';
import UsersPage from './components/users/UsersPage';
import { PermissionProvider, Permissions, usePermissions } from './contexts/PermissionContext';
import { resolvePermissionLoadState } from './contexts/permissionGate';
import Layout from './components/shared/Layout';
import AuditLogPage from './components/admin/AuditLogPage';
import UploadPage from './components/ingestion/UploadPage';
import ProjectsPage from './components/projects/ProjectsPage';
import ProjectSummaryPage from './components/projects/ProjectSummaryPage';
import ReviewPage from './components/extraction/ReviewPage';
import UnfiledPage from './components/documents/UnfiledPage';
import ProcessingPage from './components/processing/ProcessingPage';
import { Box, Typography } from '@mui/material';
import ComingSoonPage from './components/shared/ComingSoonPage';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LoadingWheel from './components/shared/LoadingWheel';
import { useTranslation } from 'react-i18next';

const ClerkSignedIn: React.FC<React.PropsWithChildren> = ({ children }) => (
  <>{SignedIn({ children })}</>
);
const ClerkSignedOut: React.FC<React.PropsWithChildren> = ({ children }) => (
  <>{SignedOut({ children })}</>
);

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable');
}

interface RouteGuardProps {
  children: React.ReactElement;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<RouteGuardProps> = ({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  fallbackPath,
}) => {
  const { t } = useTranslation();
  const {
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isLoading,
    error,
    refreshPermissions,
  } = usePermissions();

  const loadState = resolvePermissionLoadState(isLoading, error);
  if (loadState === 'loading') {
    return (
      <>
        <ClerkSignedIn><LoadingWheel /></ClerkSignedIn>
        <ClerkSignedOut><Navigate to="/login" replace /></ClerkSignedOut>
      </>
    );
  }

  if (loadState === 'error') {
    return (
      <>
        <ClerkSignedIn>
          <Box sx={{ p: 3 }}>
            <Alert
              severity="error"
              action={<Button onClick={() => void refreshPermissions()}>{t('common.retry')}</Button>}
            >
              {t(error ?? 'common.error')}
            </Alert>
          </Box>
        </ClerkSignedIn>
        <ClerkSignedOut><Navigate to="/login" replace /></ClerkSignedOut>
      </>
    );
  }

  const hasAccess = (() => {
    if (requiredPermissions && requiredPermissions.length > 0) {
      return requireAll
        ? hasAllPermissions(...requiredPermissions)
        : hasAnyPermission(...requiredPermissions);
    }

    if (requiredPermission) {
      return hasPermission(requiredPermission);
    }

    return true;
  })();

  const content = hasAccess
    ? children
    : fallbackPath
      ? <Navigate to={fallbackPath} replace />
      : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: 3,
          }}
        >
          <Typography sx={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: 2 }}>
            {t('permissions.deniedTitle')}
          </Typography>
          <Typography color="text.secondary">
            {t('permissions.deniedBody')}
          </Typography>
        </Box>
      );
  
  return (
    <>
      <ClerkSignedIn>{content}</ClerkSignedIn>
      <ClerkSignedOut>
        <Navigate to="/login" replace />
      </ClerkSignedOut>
    </>
  );
};

const AppContent: React.FC = () => {
  return (
    <Box className="App">
      <Routes>
        <Route
          path="/login"
          element={
            <ClerkSignedOut>
              <Login />
            </ClerkSignedOut>
          }
        />
        <Route
          path="/accept-invitation"
          element={
            <ClerkSignedOut>
              <AcceptInvitation />
            </ClerkSignedOut>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute requiredPermission={Permissions.ROLE_MANAGE}>
              <Layout>
                <RolesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredPermission={Permissions.USER_MANAGE}>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute requiredPermission={Permissions.ADMIN_READ_AUDIT}>
              <Layout>
                <AuditLogPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upload"
          element={
            <ProtectedRoute requiredPermission={Permissions.DOCUMENT_UPLOAD}>
              <Layout fullWidth>
                <UploadPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute requiredPermission={Permissions.PROJECT_READ}>
              <Layout>
                <ProjectsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute requiredPermission={Permissions.PROJECT_READ}>
              <Layout>
                <ProjectSummaryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/review/:documentId"
          element={
            <ProtectedRoute requiredPermission={Permissions.DOCUMENT_REVIEW}>
              <Layout>
                <ReviewPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/unfiled"
          element={
            <ProtectedRoute requiredPermission={Permissions.DOCUMENT_REFILE}>
              <Layout>
                <UnfiledPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/processing"
          element={
            <ProtectedRoute requiredPermission={Permissions.PROJECT_READ}>
              <Layout fullWidth>
                <ProcessingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/conflicts"
          element={
            <ProtectedRoute requiredPermissions={[Permissions.CONFLICT_RESOLVE, Permissions.PROJECT_READ]} requireAll>
              <Layout>
                <ProjectsPage conflictsOnly />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analyses"
          element={
            <ProtectedRoute requiredPermission={Permissions.ANALYSIS_RUN}>
              <Layout>
                <ComingSoonPage titleKey="common.analyses" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/publications"
          element={
            <ProtectedRoute requiredPermission={Permissions.PUBLICATION_MANAGE}>
              <Layout>
                <ComingSoonPage titleKey="common.publications" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/email-ingestion"
          element={
            <ProtectedRoute requiredPermission={Permissions.EMAIL_RELEASE}>
              <Layout>
                <ComingSoonPage titleKey="common.emailIngestion" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/operations"
          element={
            <ProtectedRoute requiredPermission={Permissions.OPS_RECOVER}>
              <Layout>
                <ComingSoonPage titleKey="common.operations" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute requiredPermission={Permissions.SETTINGS_MANAGE}>
              <Layout>
                <ComingSoonPage titleKey="common.settings" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={(
            <>
              <ClerkSignedIn><Navigate to="/" replace /></ClerkSignedIn>
              <ClerkSignedOut><Navigate to="/login" replace /></ClerkSignedOut>
            </>
          )}
        />
      </Routes>
    </Box>
  );
};

function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY!}>
      <Router>
        <PermissionProvider>
          <AppContent />
        </PermissionProvider>
      </Router>
    </ClerkProvider>
  );
}

export default App;
